/**
 * The parts CSV upload (`docs/bugs.md` session 7 part 4, decision 3).
 *
 * Choose a file, read the preview, then save. Nothing is written until the
 * Admin presses Save, and Save sends the preview's `planHash` so the server
 * writes exactly what was shown — or refuses if the parts changed meanwhile.
 */

import { useState } from 'react';
import { api, type ApiError } from '@/lib/api';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { REFERENCE_QUERY_KEY, useBusinessUnits } from '@/lib/reference';
import { humanize } from '@/lib/format';

interface Change {
  field: string;
  old: string | null;
  new: string | null;
}

interface PlannedRow {
  line: number;
  partNumber: string;
  action: 'update' | 'add' | 'unchanged' | 'error';
  changes: Change[];
  errors: string[];
  values: Record<string, string | null> | null;
}

export interface PartsUploadPlan {
  counts: { updated: number; added: number; unchanged: number; errors: number };
  rows: PlannedRow[];
  planHash: string;
}

/** FileReader rather than `Blob.text()`, which older browsers (and jsdom) lack. */
function readText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error ?? new Error('The file could not be read.'));
    reader.readAsText(file);
  });
}

const ACTION_VARIANT = {
  update: 'info',
  add: 'success',
  unchanged: 'outline',
  error: 'danger',
} as const;

export function PartsUpload({ onClose }: { onClose: () => void }): React.ReactElement {
  const queryClient = useQueryClient();
  const businessUnits = useBusinessUnits();
  const [csv, setCsv] = useState<string | null>(null);
  const [fileName, setFileName] = useState('');
  const [plan, setPlan] = useState<PartsUploadPlan | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [saved, setSaved] = useState<PartsUploadPlan['counts'] | null>(null);
  const [busy, setBusy] = useState(false);

  const show = (field: string, value: string | null): string => {
    if (value === null || value === '') return '—';
    if (field === 'business_unit') return businessUnits.label(value);
    if (field === 'family' || field === 'lifecycle_status') return humanize(value);
    return value;
  };

  const preview = async (text: string) => {
    setBusy(true);
    setProblem(null);
    setSaved(null);
    try {
      setPlan(
        await api<PartsUploadPlan>('/api/admin/reference/products/upload/preview', {
          method: 'POST',
          body: { csv: text },
        }),
      );
    } catch (error) {
      setPlan(null);
      setProblem((error as ApiError).message);
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!plan || csv === null) return;
    setBusy(true);
    setProblem(null);
    try {
      const result = await api<{ counts: PartsUploadPlan['counts'] }>(
        '/api/admin/reference/products/upload/commit',
        { method: 'POST', body: { csv, planHash: plan.planHash } },
      );
      setSaved(result.counts);
      setPlan(null);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'products'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'business-units'] });
      void queryClient.invalidateQueries({ queryKey: REFERENCE_QUERY_KEY });
    } catch (error) {
      setProblem((error as ApiError).message);
    } finally {
      setBusy(false);
    }
  };

  const shown = (plan?.rows ?? []).filter((row) => row.action !== 'unchanged');
  const writes = plan ? plan.counts.updated + plan.counts.added : 0;

  return (
    <div className="mb-3 space-y-3 rounded-md border bg-muted/30 p-3 text-sm">
      <p className="text-xs text-muted-foreground">
        Upload a CSV with a header row and these columns, in this order:{' '}
        <code>part_number, description, family, business_unit, lifecycle_status</code>. Rows match
        existing parts by exact part number: a match is updated, anything else is added. Parts
        missing from the file are left alone — the upload never deletes; retire a part by setting
        its lifecycle status. A blank description leaves the current one. You see a preview first,
        and nothing is saved until you press Save. Rows with errors are never saved.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <input
          type="file"
          accept=".csv,text/csv"
          aria-label="Parts CSV file"
          className="text-xs"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (!file) return;
            setFileName(file.name);
            void readText(file).then((text) => {
              setCsv(text);
              void preview(text);
            });
          }}
        />
        <Button size="sm" variant="ghost" onClick={onClose}>
          Close
        </Button>
      </div>

      {busy && <p className="text-xs text-muted-foreground">Working…</p>}

      {problem !== null && (
        <p
          role="alert"
          className="rounded-md border border-destructive/40 bg-red-50 p-2 text-xs text-red-900"
        >
          {problem}
        </p>
      )}

      {saved !== null && (
        <p role="status" className="text-xs">
          Saved: {saved.updated} updated, {saved.added} added. {saved.unchanged} unchanged,{' '}
          {saved.errors} with errors not saved.
        </p>
      )}

      {plan !== null && (
        <div className="space-y-2">
          <p aria-label="Preview counts" className="text-xs">
            Preview of {fileName}: <strong>{plan.counts.updated}</strong> updated,{' '}
            <strong>{plan.counts.added}</strong> added, <strong>{plan.counts.unchanged}</strong>{' '}
            unchanged, <strong>{plan.counts.errors}</strong> with errors.
          </p>
          {shown.length > 0 && (
            <table className="w-full text-xs">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th scope="col" className="py-1">
                    Line
                  </th>
                  <th scope="col" className="py-1">
                    Part number
                  </th>
                  <th scope="col" className="py-1">
                    Result
                  </th>
                  <th scope="col" className="py-1">
                    Changes
                  </th>
                </tr>
              </thead>
              <tbody>
                {shown.map((row) => (
                  <tr key={`${row.line}-${row.partNumber}`} className="border-t align-top">
                    <td className="py-1 tabular-nums">{row.line}</td>
                    <td className="py-1 font-mono">{row.partNumber || '—'}</td>
                    <td className="py-1">
                      <Badge variant={ACTION_VARIANT[row.action]}>{humanize(row.action)}</Badge>
                    </td>
                    <td className="py-1">
                      {row.action === 'error' && (
                        <ul>
                          {row.errors.map((error) => (
                            <li key={error} className="text-red-900">
                              {error}
                            </li>
                          ))}
                        </ul>
                      )}
                      {row.action === 'update' && (
                        <ul>
                          {row.changes.map((change) => (
                            <li key={change.field}>
                              {humanize(change.field)}: {show(change.field, change.old)} →{' '}
                              {show(change.field, change.new)}
                            </li>
                          ))}
                        </ul>
                      )}
                      {row.action === 'add' && row.values && (
                        <span>
                          {show('business_unit', row.values['business_unit'] ?? null)} ·{' '}
                          {show('family', row.values['family'] ?? null)} ·{' '}
                          {show('lifecycle_status', row.values['lifecycle_status'] ?? null)}
                          {row.values['description'] ? ` · ${row.values['description']}` : ''}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="flex gap-2">
            <Button size="sm" disabled={busy || writes === 0} onClick={() => void save()}>
              Save {writes} change{writes === 1 ? '' : 's'}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setPlan(null);
                setCsv(null);
              }}
            >
              Discard
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
